import { useEffect, useState, useMemo } from 'react'
import { paymentService, Payment } from '@/services/paymentService'
import useAppConfigStore from '@/stores/appConfigStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { DetailHeader } from '@/components/ui/DetailHeader'

const PaymentsPage = () => {
  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilters, setActiveFilters] = useState<any[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  })
  const { systemConfig } = useAppConfigStore()

  const loadPayments = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await paymentService.getAll()
      if (response.success) {
        setPayments(response.data)
        setPagination(prev => ({
          ...prev,
          total: response.data.length
        }))
      } else {
        setError('Échec du chargement des paiements')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPayments()
  }, [])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatCurrency = (amount: number, currency?: string) => {
    const currencyCode = currency || systemConfig?.default_currency || ''
    if (!currencyCode) return amount.toFixed(2)
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode
    }).format(amount)
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'En attente'
      case 'completed': return 'Complété'
      case 'failed': return 'Échoué'
      case 'refunded': return 'Remboursé'
      default: return status
    }
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending': return 'secondary'
      case 'completed': return 'default'
      case 'failed': return 'destructive'
      case 'refunded': return 'outline'
      default: return 'default'
    }
  }

  const getMethodLabel = (method: string) => {
    switch (method.toLowerCase()) {
      case 'credit_card': return 'Carte de crédit'
      case 'debit_card': return 'Carte de débit'
      case 'paypal': return 'PayPal'
      case 'bank_transfer': return 'Virement bancaire'
      case 'cash': return 'Espèces'
      default: return method
    }
  }

  const columns = useMemo(() => [
    {
      key: 'user',
      label: 'Utilisateur',
      sortable: true,
      render: (row: Payment) => (
        <div className="font-medium">
          {row.user_first_name && row.user_last_name
            ? `${row.user_first_name} ${row.user_last_name}`
            : `Utilisateur ${row.user_id.substring(0, 8)}...`}
        </div>
      )
    },
    {
      key: 'reservation_id',
      label: 'Réservation',
      sortable: true,
      render: (value: string) => (
        <div className="font-mono text-sm">{value.substring(0, 8)}...</div>
      )
    },
    {
      key: 'amount',
      label: 'Montant',
      sortable: true,
      render: (value: number, row: Payment) => (
        <div className="font-bold">{formatCurrency(value, row.currency)}</div>
      )
    },
    {
      key: 'payment_method',
      label: 'Méthode',
      sortable: true,
      render: (value: string) => (
        <Badge variant="outline">{getMethodLabel(value)}</Badge>
      )
    },
    {
      key: 'payment_status',
      label: 'Statut',
      sortable: true,
      render: (value: string) => (
        <Badge variant={getStatusBadgeVariant(value)}>
          {getStatusLabel(value)}
        </Badge>
      )
    },
    {
      key: 'transaction_id',
      label: 'Transaction ID',
      sortable: true,
      render: (value: string | null) => (
        <div className="font-mono text-sm">{value || 'N/D'}</div>
      )
    },
    {
      key: 'created_at',
      label: 'Créé le',
      sortable: true,
      render: (value: string) => formatDate(value)
    }
  ], [systemConfig])

  const actions = useMemo(() => [
    {
      key: 'view',
      label: 'Voir détails',
      icon: 'Eye',
      variant: 'outline' as const,
      size: 'sm' as const,
      onClick: (row: Payment) => {
        console.log('View payment:', row.payment_id)
        // Navigate to payment details
      }
    }
  ], [])

  const filterOptions = useMemo(() => [
    {
      key: 'payment_status',
      label: 'Statut',
      type: 'select' as const,
      options: [
        { value: 'pending', label: 'En attente' },
        { value: 'completed', label: 'Complété' },
        { value: 'failed', label: 'Échoué' },
        { value: 'refunded', label: 'Remboursé' }
      ]
    },
    {
      key: 'payment_method',
      label: 'Méthode',
      type: 'select' as const,
      options: [
        { value: 'credit_card', label: 'Carte de crédit' },
        { value: 'debit_card', label: 'Carte de débit' },
        { value: 'paypal', label: 'PayPal' },
        { value: 'bank_transfer', label: 'Virement bancaire' },
        { value: 'cash', label: 'Espèces' }
      ]
    },
    {
      key: 'search',
      label: 'Recherche',
      type: 'text' as const,
      placeholder: 'Rechercher par utilisateur ou transaction...'
    }
  ], [])

  const filteredData = useMemo(() => {
    let result = [...payments]

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(payment => 
        (payment.user_first_name && payment.user_first_name.toLowerCase().includes(query)) ||
        (payment.user_last_name && payment.user_last_name.toLowerCase().includes(query)) ||
        (payment.transaction_id && payment.transaction_id.toLowerCase().includes(query)) ||
        payment.reservation_id.toLowerCase().includes(query)
      )
    }

    if (activeFilters.length > 0) {
      activeFilters.forEach(filter => {
        if (filter.key === 'payment_status' && filter.value) {
          result = result.filter(payment => payment.payment_status === filter.value)
        }
        if (filter.key === 'payment_method' && filter.value) {
          result = result.filter(payment => payment.payment_method === filter.value)
        }
      })
    }

    return result
  }, [payments, searchQuery, activeFilters])

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize
    const endIndex = startIndex + pagination.pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, pagination.page, pagination.pageSize])

  const stats = useMemo(() => {
    const completedPayments = payments.filter(p => p.payment_status === 'completed')
    const totalAmount = completedPayments.reduce((sum, p) => sum + p.amount, 0)
    const averageAmount = completedPayments.length > 0 
      ? totalAmount / completedPayments.length 
      : 0

    return {
      total: payments.length,
      completed: completedPayments.length,
      totalAmount,
      averageAmount,
      byStatus: payments.reduce((acc, payment) => {
        acc[payment.payment_status] = (acc[payment.payment_status] || 0) + 1
        return acc
      }, {} as Record<string, number>),
      byMethod: payments.reduce((acc, payment) => {
        acc[payment.payment_method] = (acc[payment.payment_method] || 0) + 1
        return acc
      }, {} as Record<string, number>)
    }
  }, [payments, systemConfig])

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }))
  }

  const handlePageSizeChange = (newSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newSize, page: 1 }))
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleFilterChange = (filters: any[]) => {
    setActiveFilters(filters)
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleRefresh = () => {
    loadPayments()
  }

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Paiements"
        subtitle="Gérez les paiements des utilisateurs"
        breadcrumbs={[
          { label: 'Dashboard', href: '/' },
          { label: 'Paiements', href: '/payments' }
        ]}
        actions={[
          {
            label: loading ? 'Chargement...' : 'Actualiser',
            onClick: handleRefresh,
            disabled: loading
          }
        ]}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Total paiements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Montant total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.totalAmount, payments[0]?.currency || systemConfig?.default_currency || '')}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Paiements complétés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completed}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Montant moyen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.averageAmount, payments[0]?.currency || systemConfig?.default_currency || '')}
            </div>
          </CardContent>
        </Card>
      </div>

      <FilterBar
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        searchPlaceholder="Rechercher par utilisateur ou transaction..."
      />

      <DataTable
        data={paginatedData}
        columns={columns}
        actions={actions}
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: filteredData.length,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
        emptyMessage="Aucun paiement trouvé"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Répartition par statut</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(stats.byStatus).map(([status, count]) => {
                const amount = payments
                  .filter(p => p.payment_status === status)
                  .reduce((sum, p) => sum + p.amount, 0)
                const percentage = payments.length > 0 
                  ? Math.round((count / payments.length) * 100) 
                  : 0
                return (
                  <div key={status}>
                    <div className="flex justify-between mb-1">
                      <div>
                        <span className="font-medium">{getStatusLabel(status)}</span>
                        <span className="ml-2 text-sm text-muted-foreground">
                          {formatCurrency(amount, payments[0]?.currency || systemConfig?.default_currency || '')}
                        </span>
                      </div>
                      <span className="text-sm font-medium">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary-500 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Répartition par méthode</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(stats.byMethod).map(([method, count]) => {
                const amount = payments
                  .filter(p => p.payment_method === method)
                  .reduce((sum, p) => sum + p.amount, 0)
                const percentage = payments.length > 0 
                  ? Math.round((count / payments.length) * 100) 
                  : 0
                return (
                  <div key={method}>
                    <div className="flex justify-between mb-1">
                      <div>
                        <span className="font-medium">{getMethodLabel(method)}</span>
                        <span className="ml-2 text-sm text-muted-foreground">
                          {formatCurrency(amount, payments[0]?.currency || systemConfig?.default_currency || '')}
                        </span>
                      </div>
                      <span className="text-sm font-medium">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-secondary-500 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Statistiques détaillées</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <h3 className="font-medium">Top 5 des paiements</h3>
              {payments
                .filter(p => p.payment_status === 'completed')
                .sort((a, b) => b.amount - a.amount)
                .slice(0, 5)
                .map((payment, index) => (
                  <div key={payment.payment_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className="h-8 w-8 flex items-center justify-center bg-primary-100 text-primary-700 rounded-md mr-3">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium">
                          {payment.user_first_name} {payment.user_last_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {formatDate(payment.created_at)}
                        </div>
                      </div>
                    </div>
                    <div className="font-bold">
                      {formatCurrency(payment.amount, payment.currency)}
                    </div>
                  </div>
                ))}
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Activité récente</h3>
              {payments
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .slice(0, 5)
                .map((payment) => (
                  <div key={payment.payment_id} className="p-3 border rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{getStatusLabel(payment.payment_status)}</div>
                        <div className="text-sm text-muted-foreground">
                          {formatCurrency(payment.amount, payment.currency)}
                        </div>
                      </div>
                      <Badge variant={getStatusBadgeVariant(payment.payment_status)}>
                        {getMethodLabel(payment.payment_method)}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      {formatDate(payment.created_at)}
                    </div>
                  </div>
                ))}
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Résumé</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Paiements en attente</span>
                  <span className="font-bold">
                    {payments.filter(p => p.payment_status === 'pending').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Paiements échoués</span>
                  <span className="font-bold">
                    {payments.filter(p => p.payment_status === 'failed').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Paiements remboursés</span>
                  <span className="font-bold">
                    {payments.filter(p => p.payment_status === 'refunded').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Montant remboursé</span>
                  <span className="font-bold">
                    {formatCurrency(
                      payments
                        .filter(p => p.payment_status === 'refunded')
                        .reduce((sum, p) => sum + p.amount, 0),
                      payments[0]?.currency || systemConfig?.default_currency || ''
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default PaymentsPage
